import React, { useRef, useEffect } from "react";
import { Box, Typography, keyframes } from "@mui/material";
import ReactMarkdown from "react-markdown";

// Create a pulsing animation for typing indicator
const pulse = keyframes`
  0% { transform: scale(0.95); opacity: 0.4; }
  50% { transform: scale(1.05); opacity: 0.7; }
  100% { transform: scale(0.95); opacity: 0.4; }
`;

const TypingIndicator = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      p: 1.5,
      pl: 2,
      justifyContent: "flex-start",
    }}
  >
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: "50%",
        backgroundColor: "#1976d2",
        animation: `${pulse} 1.2s ease-in-out infinite`,
      }}
    />
    <Typography variant="body2" color="text.secondary" sx={{ ml: 0 }}>
      AI is typing...
    </Typography>
  </Box>
);

const MessageBubble = ({ message, direction }) => {
  const isOutgoing = direction === "outgoing";

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isOutgoing ? "flex-end" : "flex-start",
        mb: 1,
        px: 2,
      }}
    >
      <Box
        sx={{
          backgroundColor: isOutgoing ? "#1976d2" : "#f1f3f4",
          color: isOutgoing ? "white" : "text.primary",
          borderRadius: 2,
          px: 2,
          py: 1.5,
          maxWidth: "70%",
          wordBreak: "break-word",
          "& p": {
            margin: 0,
            lineHeight: 1.5,
          },
          "& p + p": {
            marginTop: 1,
          },
          "& pre": {
            backgroundColor: isOutgoing ? "rgba(255,255,255,0.1)" : "#e8eaf6",
            padding: 1,
            borderRadius: 1,
            overflow: "auto",
            fontSize: "0.85em",
          },
          "& code": {
            backgroundColor: isOutgoing ? "rgba(255,255,255,0.2)" : "#e8eaf6",
            padding: "2px 4px",
            borderRadius: "3px",
            fontSize: "0.9em",
          },
          "& ul, & ol": {
            paddingLeft: 2,
            margin: "8px 0",
          },
          "& li": {
            marginBottom: 0.5,
          },
        }}
      >
        {message ? (
          <ReactMarkdown>{message}</ReactMarkdown>
        ) : (
          <Typography
            variant="body2"
            sx={{ fontStyle: "italic", opacity: 0.7 }}
          >
            ...
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const MiniMessages = ({ messages = [], isTyping = false }) => {
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping]);

  return (
    <Box
      sx={{
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": {
          width: 6,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#c1c1c1",
          borderRadius: 3,
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#a8a8a8",
        },
      }}
    >
      <Box sx={{ py: 2 }}>
        {messages.map((msg, index) => (
          <MessageBubble
            key={index}
            message={msg.message}
            direction={msg.direction}
          />
        ))}

        {isTyping && <TypingIndicator />}

        <div ref={messagesEndRef} />
      </Box>
    </Box>
  );
};

export default MiniMessages;
